import type { RefObject } from 'react';

import { useRef, useEffect, useCallback } from 'react';
import { usePopoverHover } from 'minimal-shared/hooks';

// ----------------------------------------------------------------------

export interface UseHoverMenuOptions {
  /**
   * Delay in milliseconds before closing the menu after mouse leaves.
   * This allows users to move their cursor from the trigger to the submenu
   * without the menu closing.
   * @default 150
   */
  closeDelay?: number;
}

export interface UseHoverMenuReturn<T extends HTMLElement = HTMLElement> {
  /** Whether the menu is currently open */
  open: boolean;
  /** The anchor element for the menu */
  anchorEl: T | null;
  /** Ref to attach to the trigger element */
  elementRef: RefObject<T>;
  /** Call to open the menu (also cancels any pending close) */
  onOpen: () => void;
  /** Call to schedule closing the menu with delay */
  onClose: () => void;
  /** Call to cancel any pending close (useful when entering submenu) */
  cancelClose: () => void;
  /** Call to close immediately without delay */
  closeImmediately: () => void;
}

/**
 * Hook for managing hover-based menu state with delayed closing.
 *
 * This hook extends `usePopoverHover` to add a configurable delay before
 * closing, which creates a smooth experience when users move their cursor
 * between the trigger element and the submenu content.
 *
 * @example
 * ```tsx
 * const { open, anchorEl, elementRef, onOpen, onClose } = useHoverMenu();
 *
 * return (
 *   <>
 *     <MenuItem
 *       ref={elementRef}
 *       onMouseEnter={onOpen}
 *       onMouseLeave={onClose}
 *       selected={open}
 *     >
 *       Hover me
 *     </MenuItem>
 *
 *     <Menu
 *       open={open}
 *       anchorEl={anchorEl}
 *       slotProps={{
 *         paper: {
 *           onMouseEnter: onOpen,
 *           onMouseLeave: onClose,
 *         }
 *       }}
 *     >
 *       <MenuItem>Submenu item</MenuItem>
 *     </Menu>
 *   </>
 * );
 * ```
 */
export function useHoverMenu<T extends HTMLElement = HTMLElement>(
  options: UseHoverMenuOptions = {}
): UseHoverMenuReturn<T> {
  const { closeDelay = 150 } = options;

  const { open, onOpen, onClose, anchorEl, elementRef } = usePopoverHover<T>();

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleOpen = useCallback(() => {
    cancelClose();
    onOpen();
  }, [cancelClose, onOpen]);

  const handleClose = useCallback(() => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
      closeTimeoutRef.current = null;
    }, closeDelay);
  }, [cancelClose, onClose, closeDelay]);

  const closeImmediately = useCallback(() => {
    cancelClose();
    onClose();
  }, [cancelClose, onClose]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  return {
    open,
    anchorEl,
    elementRef,
    onOpen: handleOpen,
    onClose: handleClose,
    cancelClose,
    closeImmediately,
  };
}
